import { MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, Inject } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CovanceAgGridComponent, CovanceAgGridHelper } from '../covance-ag-grid';
import { GroupingOptionItem, groupingOptions } from './../grouping-selector';
import { DocumentsCounterBase } from './models/documents-counter';
import { Category } from './models/category';
import { defaultReportSort } from './models/default-report-sort';
import { ReportDownloader } from './report-downloader';
import { DOCUMENTS_COLUMNS_TOKEN, DocumentsColumns } from './documents-columns/documents-columns';
import { UnreadReportsStorage, UNREAD_REPORTS_STORAGE } from './unread-reports-storage';
import { Report } from './models/report';
import { Settings } from '../core';

@Component({
    selector: 'reports-grid',
    template: require('./reports-grid.component.html'),
    styles: [require('./reports-grid.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ReportsGridComponent implements OnChanges, OnDestroy {

    public gridOptions: GridOptions = {
        groupRowRendererParams: {
            suppressCount: true,
            checkbox: true
        },
        rowSelection: 'multiple',
        rowDeselection: true,
        groupSelectsChildren: true,
        isRowSelectable: (params: {data: Report}) => params.data.subcategoryId !== 'sponsor-communications-e-learning',
    };

    public columns: ColDef[];

    @Input()
    public reports: Report[];

    @Input()
    public category: Category;

    @Input()
    public groupBy: GroupingOptionItem;

    @Input()
    public checkBoxValue: boolean;

    @Input()
    public showExport = false;

    @Output()
    public toggleUnread = new EventEmitter<boolean>();

    public get showPrint() {
        return this.category && this.category.canCombineForPrinting;
    }

    public get showSave() {
        return this.category && !Category.isELearningCategory(this.category.categoryId);
    }

    public rowNodes = new BehaviorSubject<Report[]>([]);

    @ViewChild('grid')
    private grid: CovanceAgGridComponent;

    private subscription: Subscription;
    private gridSortModel: Object;

    constructor(
        @Inject(DOCUMENTS_COLUMNS_TOKEN) private categoriesGridColumns: DocumentsColumns,
        private gridHelper: CovanceAgGridHelper,
        private reportDownloader: ReportDownloader,
        private settings: Settings,
        private dialog: MdlDialogService,
        @Inject(UNREAD_REPORTS_STORAGE) private readonly unreads: UnreadReportsStorage<DocumentsCounterBase>,
    ) {
        this.subscription = unreads.allUnread
            .subscribe(() => {
                if (!isEmpty(this.reports)) {
                    this.rowNodes.next([...this.reports]);
                }
            });

        this.gridOptions.getRowClass = this.getRowClass;
    }

    public onDisplayedColumnsChanged() {
        // restore the current sort after group/ungroup
        this.restoreSortModel();
    }

    public ngOnChanges(changes: SimpleChanges) {
        const reportsChanges = changes['reports'];
        const categoryChanges = changes['category'];
        const groupByChanges = changes['groupBy'];

        if (reportsChanges) {
            this.reports = reportsChanges.currentValue;
            this.rowNodes.next(this.reports);
        }

        if (categoryChanges) {
            if (this.grid) {
                this.grid.resetQuickFilter();
            }

            this.category = categoryChanges.currentValue;
        }

        if (groupByChanges) {
            this.groupBy = groupByChanges.currentValue;
        }

        if ((this.category && this.groupBy) && (categoryChanges || groupByChanges)) {

            const columns = this.categoriesGridColumns.getColumnDefinitions(this.category);
            const defaultReportSortFn = this.isSortModelEmpty() ? null : defaultReportSort;
            const groupResult = this.gridHelper.groupData(this.groupBy.columnTitle, this.reports, columns, null, defaultReportSortFn);

            this.columns = groupResult.columns;
            this.reports = groupResult.data as Report[];

            if (this.reports) {
                this.restoreDefaultSort();
                this.rowNodes.next([...this.reports]);
            }

        }
        
        if (this.category && this.reports) {
            this.saveSortModel();
        }
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public async printDocuments(data: Report[]) {
        if (!this.validateDownloadableData(data)) {
            return;
        }

        await this.reportDownloader.printReports(this.filterDownloadableData(data), this.unreads);
        this.gridOptions.api.redrawRows();
    }

    public switchUnreads(unreadOnly: boolean) {
        this.toggleUnread.emit(unreadOnly);
    }

    public async saveDocuments(data: Report[]) {
        if (!this.validateDownloadableData(data)) {
            return;
        }

        await this.reportDownloader.saveReports(this.filterDownloadableData(data), this.unreads);
        this.gridOptions.api.redrawRows();
    }

    private validateDownloadableData(data: Report[]): boolean {
        if (data && data.length > this.settings.maxNumberOfDocumentsForDownload) {
            this.dialog.alert(`Too many documents have been selected. Max number of documents is ${this.settings.maxNumberOfDocumentsForDownload}`);
            return false;
        }
        return true;
    }

    private filterDownloadableData(data: Report[]) {
        return data.filter(r => !Category.isELearningCategory(r.subcategoryId));
    }

    private getRowClass = (params: { data: Report }) => {
        if (params.data && !params.data.isReadByMe) {
            return 'strong-row';
        }
        return null;
    }

    private saveSortModel() {
        if (this.gridOptions.api) {
            this.gridSortModel = this.gridOptions.api.getSortModel();
        }
    }

    private restoreSortModel() {
        if (this.gridSortModel) {
            this.gridOptions.api.setSortModel(this.gridSortModel);
        }
    }

    private isSortModelEmpty(): boolean {
        return !this.gridOptions.api || isEmpty(this.gridOptions.api.getSortModel());
    }

    private restoreDefaultSort() {
        if (this.groupBy === groupingOptions.none && this.isSortModelEmpty()) {
            this.reports = this.reports.sort((a, b) => defaultReportSort(a, b));
        }
    }
}
